import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChainVerification,
  LedgerEvent,
  listLedgerEvents,
  verifyLedger,
} from "../../api";
import { APP_ROUTES } from "../../routes";

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}

export default function LedgerViewer() {
  const { id: projectId = "" } = useParams<{ id: string }>();
  const [events, setEvents] = useState<LedgerEvent[]>([]);
  const [verification, setVerification] = useState<ChainVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const [evts, verify] = await Promise.all([
          listLedgerEvents(projectId),
          verifyLedger(projectId),
        ]);
        if (!cancelled) {
          setEvents(evts);
          setVerification(verify);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="manifold-page">
      <header className="manifold-page-header">
        <p className="sg-section-kicker">Compliance · Intent Ledger</p>
        <h1>Compliance Ledger</h1>
        <p className="text-muted">
          Append-only, hash-chained log of every intent transition,
          deployment promotion, and fallback activation in this project.
          Each entry is Ed25519-signed and chained to its predecessor —
          backing EU AI Act Article 12 record-keeping and Article 26(6) log retention.
        </p>
      </header>

      {verification && (
        <div className={verification.ok ? "chain-banner ok" : "chain-banner bad"}>
          {verification.ok ? (
            <>
              <strong>✓ Chain verified</strong> — {verification.checked}{" "}
              {verification.checked === 1 ? "entry" : "entries"}, head{" "}
              <code>{verification.head_hash.slice(0, 16)}…</code>, key{" "}
              <code>{verification.public_key.slice(0, 16)}…</code>
              {verification.key_ephemeral && " (dev key — set LEDGER_SIGNING_KEY)"}
            </>
          ) : (
            <>
              <strong>✗ Chain verification failed</strong> —{" "}
              {verification.problems.join("; ")}
            </>
          )}
        </div>
      )}

      {error && <div className="sample-banner">Could not load ledger: {error}</div>}

      {!loading && !error && events.length === 0 && (
        <section className="manifold-empty">
          <h2>No ledger entries yet</h2>
          <p>
            Entries are written automatically when this project acts —
            starting training, creating or starting a deployment, stopping
            one, or triggering an emergency stop. Run something and the
            signed record appears here.
          </p>
        </section>
      )}

      {events.length > 0 && (
        <div className="egress-list">
          {events.map((e) => (
            <Link
              to={APP_ROUTES.complianceEvent(projectId, e.id)}
              className="egress-row ledger-row"
              key={e.id}
            >
              <time>#{e.seq} · {formatWhen(e.created_at)}</time>
              <div>
                <span className={`ledger-chip ${e.channel}`}>{e.channel}</span>{" "}
                <strong>{e.event_type}</strong> — {e.action}
                <div className="egress-dest">actor {e.actor}</div>
              </div>
              <span className="egress-sig">✓ {e.entry_hash.slice(0, 10)}…</span>
            </Link>
          ))}
        </div>
      )}

      <div className="egress-actions">
        <Link to={APP_ROUTES.egress(projectId)} className="sg-btn sg-btn-secondary">
          Family Egress view
        </Link>
        <Link to={APP_ROUTES.project(projectId)} className="sg-btn sg-btn-ghost">
          Back to project
        </Link>
      </div>
    </div>
  );
}
